import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import GetAppIcon from '@mui/icons-material/GetApp';
import CheckIcon from '@mui/icons-material/Check';
import { selectCartCount } from '../store/cartSlice';
import { selectFavouriteIds } from '../store/favouritesSlice';
import { useTheme } from '../theme/Theme';
import { storage } from '../utils/storage';
import { clearAuthSession, getStoredUser, isUserLoggedIn } from '../utils/authSession';
import { useCartMessage } from '../context/CartMessageContext';
import { RTL_LANGS } from '../i18n';

const PUBLIC_NAV_LINKS = [
  { key: 'home', labelKey: 'home', path: '/', icon: HomeOutlinedIcon },
  { key: 'search', labelKey: 'search', path: '/search', icon: SearchIcon },
  { key: 'favourites', labelKey: 'Favourites', path: '/favourites', icon: FavoriteBorderIcon, badgeKey: 'favourites' },
];

const ORDERS_NAV_LINK = {
  key: 'orders',
  labelKey: 'orders.header',
  path: '/orders',
  icon: ReceiptLongOutlinedIcon,
};

const AUTH_NAV_LINKS = [
  { key: 'login', labelKey: 'Login', path: '/signin', icon: LoginIcon },
  { key: 'register', labelKey: 'Register', path: '/register', icon: PersonAddOutlinedIcon },
];

const PROFILE_NAV_LINK = {
  key: 'profile',
  labelKey: 'Profile',
  path: '/profile',
  icon: PersonOutlinedIcon,
};

const LANGUAGES = [
  { code: 'ku', label: 'کوردی' },
  { code: 'ar', label: 'العربية' },
];

const INFO_LINKS = [
  { key: 'delivery', labelKey: 'delivery_page', path: '/delivery', icon: LocalShippingOutlinedIcon },
  { key: 'offers', labelKey: 'offers_page', path: '/offers', icon: LocalOfferOutlinedIcon },
  { key: 'download', labelKey: 'download_app_page', path: '/download', icon: GetAppIcon },
];

const BOTTOM_NAV_ROUTES = {
  home: '/',
  search: '/search',
  favourites: '/favourites',
  cart: '/cart',
  profile: '/profile',
  login: '/signin',
};

function getBottomNavValue(pathname, loggedIn) {
  if (pathname === '/' || pathname.startsWith('/category')) return 'home';
  if (pathname.startsWith('/search')) return 'search';
  if (pathname.startsWith('/favourites')) return 'favourites';
  if (pathname.startsWith('/cart')) return 'cart';
  if (pathname.startsWith('/profile') || pathname.startsWith('/orders')) return 'profile';
  if (!loggedIn && (pathname.startsWith('/signin') || pathname.startsWith('/register'))) return 'login';
  return 'home';
}

const MOBILE_DRAWER_PAPER_SX = {
  width: '96vw',
  minWidth: '96vw',
  maxWidth: 480,
  flexShrink: 0,
  boxSizing: 'border-box',
  overflowX: 'hidden',
};

function SettingsSectionLabel({ icon: Icon, title }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2.5,
          bgcolor: alpha('#B8894A', 0.08),
          border: 1,
          borderColor: alpha('#B8894A', 0.14),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 22, color: 'primary.main' }} />
      </Box>
      <Typography variant="body1" fontWeight={800} color="primary.main">
        {title}
      </Typography>
    </Stack>
  );
}

const settingsToggleSx = {
  bgcolor: alpha('#B8894A', 0.06),
  border: 1,
  borderColor: alpha('#B8894A', 0.14),
  borderRadius: 3,
  p: 0.4,
  '& .MuiToggleButton-root': {
    border: 0,
    borderRadius: '10px !important',
    flex: 1,
    py: 1.25,
    px: 1.5,
    fontWeight: 700,
    fontSize: '0.95rem',
    textTransform: 'none',
    color: 'text.secondary',
    gap: 0.75,
    transition: 'all 0.2s ease',
    '&.Mui-selected': {
      bgcolor: 'primary.main',
      color: '#fff',
      boxShadow: '0 4px 14px rgba(184,137,74,0.28)',
      '&:hover': { bgcolor: 'primary.dark' },
    },
    '&:hover': {
      bgcolor: alpha('#B8894A', 0.08),
    },
  },
};

function LanguageToggle({ value, onChange, fullWidth = false }) {
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_, code) => code && onChange(code)}
      size="medium"
      fullWidth={fullWidth}
      sx={settingsToggleSx}
    >
      {LANGUAGES.map((lang) => (
        <ToggleButton key={lang.code} value={lang.code}>
          {lang.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

function ThemeToggle({ isDarkMode, onToggle, lightLabel, darkLabel, fullWidth = false }) {
  const mode = isDarkMode ? 'dark' : 'light';

  return (
    <ToggleButtonGroup
      value={mode}
      exclusive
      onChange={(_, next) => {
        if (!next || next === mode) return;
        onToggle();
      }}
      size="medium"
      fullWidth={fullWidth}
      sx={settingsToggleSx}
    >
      <ToggleButton value="light">{lightLabel}</ToggleButton>
      <ToggleButton value="dark">{darkLabel}</ToggleButton>
    </ToggleButtonGroup>
  );
}

function isNavActive(pathname, path) {
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
}

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const cartCount = useSelector(selectCartCount);
  const favouriteCount = useSelector(selectFavouriteIds).length;
  const { showCartMessage } = useCartMessage();
  const { isDarkMode, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [langAnchor, setLangAnchor] = useState(null);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');

  const refreshAuth = useCallback(async () => {
    const isLogged = await isUserLoggedIn();
    setLoggedIn(isLogged);
    if (isLogged) {
      const user = await getStoredUser();
      setUserName(user?.name ?? (await storage.getItem('userName')) ?? '');
    } else {
      setUserName('');
    }
  }, []);

  useEffect(() => {
    refreshAuth();
    const onProfileUpdated = () => refreshAuth();
    window.addEventListener('profile-updated', onProfileUpdated);
    return () => window.removeEventListener('profile-updated', onProfileUpdated);
  }, [refreshAuth]);

  const changeLanguage = async (code) => {
    if (!code || code === i18n.language) return;
    await i18n.changeLanguage(code);
    await storage.setItem('userLanguage', code);
    document.documentElement.dir = RTL_LANGS.includes(code) ? 'rtl' : 'ltr';
    setLangAnchor(null);
  };

  const toolbarIconSx = {
    border: 1,
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: '0 2px 8px rgba(184,137,74,0.1)',
    '&:hover': { bgcolor: alpha('#B8894A', 0.06), borderColor: 'primary.light' },
  };

  const navLinks = loggedIn
    ? [...PUBLIC_NAV_LINKS, ORDERS_NAV_LINK, PROFILE_NAV_LINK]
    : [...PUBLIC_NAV_LINKS, ...AUTH_NAV_LINKS];

  const drawerNavLinks = loggedIn
    ? [...PUBLIC_NAV_LINKS, ORDERS_NAV_LINK, PROFILE_NAV_LINK]
    : PUBLIC_NAV_LINKS;

  const handleBottomNav = (_, value) => {
    if (!value) return;
    if (value === 'cart' && cartCount === 0) {
      showCartMessage(t('cart_empty_message'), 'info');
      return;
    }
    if (value === 'profile' && !loggedIn) {
      navigate('/signin');
      return;
    }
    navigate(BOTTOM_NAV_ROUTES[value]);
  };

  const handleCartNav = useCallback((e) => {
    if (cartCount === 0) {
      e.preventDefault();
      showCartMessage(t('cart_empty_message'), 'info');
      return;
    }
    navigate('/cart');
  }, [cartCount, navigate, showCartMessage, t]);

  const bottomNavValue = getBottomNavValue(location.pathname, loggedIn);

  const closeDrawer = () => setDrawerOpen(false);

  const handleLogout = async () => {
    await clearAuthSession();
    setLoggedIn(false);
    setUserName('');
    setAccountAnchor(null);
    closeDrawer();
    navigate('/');
  };

  const goTo = (path) => {
    setAccountAnchor(null);
    closeDrawer();
    navigate(path);
  };


  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg" disableGutters={isMobile}>
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, md: 64 }, px: { xs: 1, md: 0 }, gap: 1 }}>
            {isMobile ? (
              <IconButton edge="start" onClick={() => setDrawerOpen(true)} aria-label="menu" sx={{ ml: 0.5, width: 44, height: 44 }}>
                <MenuIcon sx={{ fontSize: 28 }} />
              </IconButton>
            ) : null}

            <Box
              component={RouterLink}
              to="/"
              sx={{
                textDecoration: 'none',
                flexGrow: isMobile ? 1 : 0,
                flexShrink: 0,
                display: 'flex',
                justifyContent: isMobile ? 'center' : 'flex-start',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                userSelect: 'none',
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #D4A574, #B8894A, #8F6A35)',
                  color: '#fff',
                  px: { xs: 1.5, md: 2 },
                  py: 0.75,
                  borderRadius: 2,
                  display: 'inline-block',
                }}
              >
                <Typography variant={isMobile ? 'body2' : 'subtitle1'} fontWeight={800} noWrap>
                  {t('app_name')}
                </Typography>
              </Box>
            </Box>

            {isMobile ? (
              <IconButton onClick={handleCartNav} aria-label={t('cart_a11y')} sx={{ mr: 0.5 }}>
                <Badge badgeContent={cartCount} color="secondary" max={99}>
                  <ShoppingCartOutlinedIcon />
                </Badge>
              </IconButton>
            ) : null}

            {!isMobile && (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ flex: 1, justifyContent: 'center', alignItems: 'stretch' }}
              >
                {navLinks.map((link) => {
                  const active = isNavActive(location.pathname, link.path);
                  return (
                    <Button
                      key={link.key}
                      component={RouterLink}
                      to={link.path}
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderRadius: 0,
                        fontWeight: active ? 800 : 600,
                        fontSize: { xs: 14, md: 15.5 },
                        color: active ? 'primary.main' : 'text.secondary',
                        position: 'relative',
                        '&::after': active
                          ? {
                              content: '""',
                              position: 'absolute',
                              bottom: 0,
                              left: '20%',
                              right: '20%',
                              height: 3,
                              borderRadius: 3,
                              bgcolor: 'primary.main',
                            }
                          : {},
                        '&:hover': {
                          color: 'primary.main',
                          bgcolor: 'transparent',
                        },
                      }}
                    >
                      {link.badgeKey === 'favourites' && favouriteCount > 0 ? (
                        <Badge badgeContent={favouriteCount} color="secondary" max={99} sx={{ '& .MuiBadge-badge': { top: 4, right: -8 } }}>
                          {t(link.labelKey)}
                        </Badge>
                      ) : (
                        t(link.labelKey)
                      )}
                    </Button>
                  );
                })}
              </Stack>
            )}

            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
              {!isMobile && (
                <>
                  <IconButton onClick={(e) => setLangAnchor(e.currentTarget)} aria-label={t('Language')} sx={toolbarIconSx}>
                    <TranslateIcon fontSize="small" color="primary" />
                  </IconButton>

                  <IconButton onClick={toggleTheme} aria-label={t('Theme')} sx={toolbarIconSx}>
                    {isDarkMode ? <DarkModeOutlinedIcon fontSize="small" color="primary" /> : <LightModeOutlinedIcon fontSize="small" color="primary" />}
                  </IconButton>

                  <IconButton onClick={handleCartNav} aria-label={t('cart_a11y')} size="small">
                    <Badge badgeContent={cartCount} color="secondary" max={99}>
                      <ShoppingCartOutlinedIcon />
                    </Badge>
                  </IconButton>
                </>
              )}
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Menu
        anchorEl={langAnchor}
        open={Boolean(langAnchor)}
        onClose={() => setLangAnchor(null)}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: 3,
              border: 1,
              borderColor: alpha('#B8894A', 0.14),
              boxShadow: '0 8px 28px rgba(184,137,74,0.16)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: alpha('#B8894A', 0.06),
            borderBottom: 1,
            borderColor: alpha('#B8894A', 0.1),
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <TranslateIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="body2" fontWeight={800} color="primary.main">
              {t('Language')}
            </Typography>
          </Stack>
        </Box>
        {LANGUAGES.map((lang) => {
          const selected = i18n.language === lang.code;
          return (
            <MenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              selected={selected}
              sx={{
                py: 1.25,
                fontWeight: selected ? 800 : 600,
                color: selected ? 'primary.main' : 'text.primary',
                '&.Mui-selected': {
                  bgcolor: alpha('#B8894A', 0.08),
                  '&:hover': { bgcolor: alpha('#B8894A', 0.12) },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {selected ? (
                  <CheckIcon fontSize="small" color="primary" />
                ) : (
                  <Box sx={{ width: 20 }} />
                )}
              </ListItemIcon>
              {lang.label}
            </MenuItem>
          );
        })}
      </Menu>

      <Menu
        anchorEl={accountAnchor}
        open={Boolean(accountAnchor)}
        onClose={() => setAccountAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {loggedIn ? (
          [
            <Box key="header" sx={{ px: 2, py: 1.5, minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary">
                {t('Welcome')}
              </Typography>
              <Typography fontWeight={700}>{userName || t('Account')}</Typography>
            </Box>,
            <Divider key="divider" />,
            <MenuItem key="logout" onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              {t('logout')}
            </MenuItem>,
          ]
        ) : (
          [
            <MenuItem key="login" onClick={() => goTo('/signin')}>
              <ListItemIcon>
                <LoginIcon fontSize="small" />
              </ListItemIcon>
              {t('Login')}
            </MenuItem>,
            <MenuItem key="register" onClick={() => goTo('/register')}>
              <ListItemIcon>
                <PersonAddOutlinedIcon fontSize="small" />
              </ListItemIcon>
              {t('Register')}
            </MenuItem>,
          ]
        )}
      </Menu>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: MOBILE_DRAWER_PAPER_SX,
        }}
        sx={{
          '& .MuiDrawer-paper': MOBILE_DRAWER_PAPER_SX,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minWidth: 0 }}>
          <Box
            sx={{
              px: 2.5,
              py: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #D4A574, #B8894A)',
              color: '#fff',
            }}
          >
            <Box>
              <Typography fontWeight={800} sx={{ fontSize: { xs: '1.25rem', sm: '1.35rem' } }}>
                {t('app_name')}
              </Typography>
              {loggedIn && (
                <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.25, fontWeight: 600 }}>
                  {t('Welcome')}, {userName || t('Account')}
                </Typography>
              )}
            </Box>
            <IconButton onClick={closeDrawer} sx={{ color: '#fff', width: 44, height: 44 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <List sx={{ px: 2, py: 2 }}>
              {drawerNavLinks.map((link) => {
                const active = isNavActive(location.pathname, link.path);
                const Icon = link.icon;
                return (
                  <ListItemButton
                    key={link.key}
                    component={RouterLink}
                    to={link.path}
                    onClick={closeDrawer}
                    selected={active}
                    sx={{
                      borderRadius: 3,
                      mb: 0.75,
                      py: 1.75,
                      px: 1.5,
                      minHeight: 56,
                      justifyContent: 'flex-start',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: '#fff',
                        '& .MuiListItemIcon-root': { color: '#fff' },
                        '&:hover': { bgcolor: 'primary.dark' },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {link.badgeKey === 'favourites' && favouriteCount > 0 ? (
                        <Badge badgeContent={favouriteCount} color="secondary" max={99}>
                          <Icon />
                        </Badge>
                      ) : (
                        <Icon />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={t(link.labelKey)}
                      sx={{ flex: '0 1 auto', my: 0 }}
                      primaryTypographyProps={{
                        fontWeight: active ? 800 : 700,
                        fontSize: '1.05rem',
                        sx: { wordBreak: 'break-word', textAlign: 'start' },
                      }}
                    />
                  </ListItemButton>
                );
              })}
              <ListItemButton
                onClick={(e) => {
                  closeDrawer();
                  handleCartNav(e);
                }}
                selected={isNavActive(location.pathname, '/cart')}
                sx={{
                  borderRadius: 3,
                  mb: 0.75,
                  py: 1.75,
                  px: 1.5,
                  minHeight: 56,
                  justifyContent: 'flex-start',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Badge badgeContent={cartCount} color="secondary" max={99}>
                    <ShoppingCartOutlinedIcon />
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={t('cart_title')}
                  sx={{ flex: '0 1 auto', my: 0 }}
                  primaryTypographyProps={{
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    sx: { wordBreak: 'break-word', textAlign: 'start' },
                  }}
                />
              </ListItemButton>
              {INFO_LINKS.map((link) => {
                const active = isNavActive(location.pathname, link.path);
                const Icon = link.icon;
                return (
                  <ListItemButton
                    key={link.key}
                    component={RouterLink}
                    to={link.path}
                    onClick={closeDrawer}
                    selected={active}
                    sx={{
                      borderRadius: 3,
                      mb: 0.75,
                      py: 1.75,
                      px: 1.5,
                      minHeight: 56,
                      justifyContent: 'flex-start',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: '#fff',
                        '& .MuiListItemIcon-root': { color: '#fff' },
                        '&:hover': { bgcolor: 'primary.dark' },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Icon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t(link.labelKey)}
                      sx={{ flex: '0 1 auto', my: 0 }}
                      primaryTypographyProps={{
                        fontWeight: active ? 800 : 700,
                        fontSize: '1.05rem',
                        sx: { wordBreak: 'break-word', textAlign: 'start' },
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>

            <Divider sx={{ mx: 2.5 }} />

            <Box sx={{ px: 2.5, py: 2.5 }}>
              <Stack spacing={2}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha('#B8894A', 0.04),
                    borderColor: alpha('#B8894A', 0.12),
                  }}
                >
                  <SettingsSectionLabel icon={TranslateIcon} title={t('Language')} />
                  <LanguageToggle value={i18n.language} onChange={changeLanguage} fullWidth />
                </Paper>

                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha('#B8894A', 0.04),
                    borderColor: alpha('#B8894A', 0.12),
                  }}
                >
                  <SettingsSectionLabel
                    icon={isDarkMode ? DarkModeOutlinedIcon : LightModeOutlinedIcon}
                    title={t('Theme')}
                  />
                  <ThemeToggle
                    isDarkMode={isDarkMode}
                    onToggle={toggleTheme}
                    lightLabel={t('Light Mode')}
                    darkLabel={t('Dark Mode')}
                    fullWidth
                  />
                </Paper>
              </Stack>
            </Box>
          </Box>

          <Box sx={{ p: 2.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            {loggedIn ? (
              <Button fullWidth variant="outlined" color="error" size="large" startIcon={<LogoutIcon />} onClick={handleLogout} sx={{ borderRadius: 3, py: 1.5, fontWeight: 700 }}>
                {t('logout')}
              </Button>
            ) : (
              <Stack spacing={1.25}>
                <Button fullWidth variant="contained" size="large"  onClick={() => goTo('/signin')} sx={{ borderRadius: 3, py: 1.5, fontWeight: 700 }}>
                  {t('Login')}
                </Button>
                <Button fullWidth variant="outlined" size="large"  onClick={() => goTo('/register')} sx={{ borderRadius: 3, py: 1.5, fontWeight: 700 }}>
                  {t('Register')}
                </Button>
              </Stack>
            )}
          </Box>
        </Box>
      </Drawer>

      {isMobile && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            borderTop: 1,
            borderColor: 'divider',
            pb: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <BottomNavigation
            value={bottomNavValue}
            onChange={handleBottomNav}
            showLabels
            sx={{
              height: 64,
              '& .MuiBottomNavigationAction-root': {
                minWidth: 0,
                px: 0.5,
                '&.Mui-selected': { color: 'primary.main' },
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: 11,
                fontWeight: 600,
                '&.Mui-selected': { fontSize: 11, fontWeight: 800 },
              },
            }}
          >
            <BottomNavigationAction
              value="home"
              label={t('home')}
              icon={<HomeOutlinedIcon />}
            />
            <BottomNavigationAction
              value="search"
              label={t('search')}
              icon={<SearchIcon />}
            />
            <BottomNavigationAction
              value="favourites"
              label={t('favourite_a11y')}
              icon={
                <Badge badgeContent={favouriteCount} color="secondary" max={99}>
                  <FavoriteBorderIcon />
                </Badge>
              }
            />
            <BottomNavigationAction
              value="cart"
              label={t('cart_title')}
              icon={
                <Badge badgeContent={cartCount} color="secondary" max={99}>
                  <ShoppingCartOutlinedIcon />
                </Badge>
              }
            />
            {loggedIn ? (
              <BottomNavigationAction
                value="profile"
                label={t('Profile')}
                icon={
                  <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: 'primary.main' }}>
                    {(userName || t('Account')).charAt(0).toUpperCase()}
                  </Avatar>
                }
              />
            ) : (
              <BottomNavigationAction
                value="login"
                label={t('Login')}
                icon={<LoginIcon />}
              />
            )}
          </BottomNavigation>
        </Paper>
      )}
    </>
  );
}
